import React, { useMemo } from 'react';
/**
 * ReferralInitiationOutcome
 * ---------------
 * What:
 *   Final outcome page after submit. Reads decision & reason from query string
 *   and renders per‑org copy (fallback defaults).
 *
 * Why:
 *   - Keep decision rendering decoupled from server response format changes.
 *   - Allow org‑specific copy and future internationalisation.
 *
 * Notes:
 *   - decision is authoritative from the server; do not compute it client‑side.
 *   - reasonCode is optional and only displayed for ineligible outcomes.
 */
import { getOrgConfig } from '../../utils/orgResolution';
import { resolveOutcomeCopy, resolveOutcomeSection, formatEligibleBody } from '../../constants/outcomeCopy';
import { useLocation, useNavigate } from 'react-router-dom';
import providerLogo from '../../assets/images/logos/lcs_logo.png';

export default function ReferralInitiationOutcome() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const decision = params.get('decision') || 'eligible';
  const reason = params.get('reason') || '';
  const copy = resolveOutcomeCopy(orgId);

  const isEligible = decision === 'eligible';
  // A referral stopped for a reason of its own (consent, say) carries its own
  // title and body, so it is never told it was the postcode.
  const section = resolveOutcomeSection(copy, isEligible, reason);
  const reasonText = !isEligible && reason ? (section.reasonMessages?.[reason] || '') : '';
  // We attempt to read the captured referrer email from localStorage to personalise
  // the eligible outcome copy. The email is written by the initiation flow when
  // the user enters their email address. If unavailable, we fall back to the static copy.
  let eligibleBody = section.body;
  if (isEligible) {
    try {
      const email = window.localStorage.getItem('emailForSignIn');
      eligibleBody = formatEligibleBody(email);
    } catch (_e) {
      eligibleBody = section.body;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <div className="sticky top-0 left-0 right-0 bg-white z-30 relative" style={{ boxShadow: '0 24px 36px rgba(255,255,255,0.95), 0 12px 20px rgba(255,255,255,0.85)' }}>
        <div className="w-full px-6 md:px-10 pt-10">
          <div className="w-full md:w-1/2 mx-auto mb-8 flex flex-col items-end gap-2">
            <img src={providerLogo} alt="LCS logo" className="h-20 w-auto opacity-90" />
          </div>
        </div>
      </div>
      <div className="h-12" />
      <div className="flex-grow w-full px-6 md:px-10 pb-28 text-left">
        <div className="w-full md:w-1/2 mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h1>
          <p className="text-gray-700 whitespace-pre-line">{isEligible ? eligibleBody : section.body}</p>
          {!!reasonText && <p className="text-gray-600 mt-3">{reasonText}</p>}
          {/* Only show CTA for ineligible outcomes - successful referrals don't need a button */}
          {!isEligible && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/rms/referral/start')}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                {section.ctaLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


